import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from "@tanstack/react-query";
import useExportData from '../../hooks/useExportData';
import useDebounce from '../../hooks/useDebounce';
import { toDateFormat } from '../../helpers/dates';
import useCleanPaginationURL from '../../hooks/useCleanPaginationURL';
import { usePaginationTextReplace } from '../../hooks/usePaginationTextReplace';
import { createDataTableFilterColumns } from '../common/TableComponent';
import { pageSize } from '../../services/settings';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import { useAlert } from '../../context/alert';
import { IconButton, Stack, TextInput } from '@primer/react'
import { metricsTableConfig, TableColumn } from '../common/TableComponent';
import { ScoringFilterModal } from './components/ScoringFilterModal';
import { FilterValues, FilterCallbacks, FilterConfig } from '../../types/common/filters';
import { Button, ActionList, ActionMenu } from '@primer/react';
import { Table, DataTable } from '@primer/react/experimental'
import { DownloadIcon, SearchIcon, XCircleFillIcon } from '@primer/octicons-react'
import { scoringTabsData } from '../navigation/Segment/Segment+Data';
import Segment from '../navigation/Segment/Segment';
import { getScoringRoleName, getTableTypeName, ScoringLeadsRequest, ScoringRole, ScoringTableType, ScoringTabType } from '../../types/api/scoring/scoring';
import { countAllUsersRange, countFavoriteAddRange, countMessages, countTimeInSubsectionRange, countWatchAdRange, scoringFilters, scoringLeadConfig, scoringSellerConfig, timeInCatalogRangeConfig, timeInSectionRangeConfig } from './ScoringList+Filter';
import { columnsForCurrentTab } from './ScoringList+Data';
import {
    countWatchRange,
    countAddFavoriteRange,
    activeAdsNumberConfig,
    archiveAdsNumberConfig,
    countTimeInSectionRange,
    scoringRange
} from './ScoringList+Filter';
import { exportScoring, fetchScoringLeads } from '../../services/scoring';
import NumberRangeFormModal, { NumberRangeGroup } from '../basic/filters/NumberRangeFormModal';
import { useFilterOptions } from '../../hooks/useFilterOptions';
import { SelectFormModal } from '../metrics/components/SelectFormModal';
import { getAppliedColumnCountryIdFilters, getAppliedColumnCountryNameFilters, useAppliedColumnCountryIdFilters, useAppliedColumnCountryNameFilters, useAppliedColumnFilters } from './ScoringFiltersManager';

export const INITIAL_FILTER_VALUES: FilterValues = {
    platform: null,
    status: null,
    category: null,
    countryResponseForName: null,
    cityResponseForName: null,
    countryResponseForId: null,
    cityResponseForId: null,
    marketingCompany: null,
    trafficSource: null,
    subcategory: null,
    buttonName: null,
    userType: null,
    withHint: null,
    withoutHint: null,
    region: null,
    sourceScreen: null,
    targetScreen: null,
    actionType: null,
    timeOfDay: null,
    contactType: null,
    filterName: null,
    screen: null,
    hint: null,
    scoringUserType: null,
    price: { from: '', to: '' },
    date: { from: '', to: '' },
    time: { from: '', to: '' },
    scoreFrom: null,
    scoreTo: null,
    viewsFrom: null,
    viewsTo: null,
    favoritesFrom: null,
    favoritesTo: null,
    messagesFrom: null,
    messagesTo: null,
    timeFrom: null,
    timeTo: null,
    activeAdsFrom: null,
    activeAdsTo: null,
    archivedAdsFrom: null,
    archivedAdsTo: null,
    totalUsersFrom: null,
    totalUsersTo: null,
    avgTimeFrom: null,
    avgTimeTo: null,
    sellersScoreFrom: null,
    sellersScoreTo: null,
    leadsScoreFrom: null,
    leadsScoreTo: null,
};

// Маппинг ID конфигурации на поля filterValues
const filterFieldsMap: Record<string, { from: keyof FilterValues; to: keyof FilterValues }> = {
    'activeAdsNumberConfig': { from: 'activeAdsFrom', to: 'activeAdsTo' },
    'archiveAdsNumberConfig': { from: 'archivedAdsFrom', to: 'archivedAdsTo' },
    'favoriteAdsNumberConfig': { from: 'favoritesFrom', to: 'favoritesTo' },
    'activeAdsRangeConfigTotal': { from: 'activeAdsFrom', to: 'activeAdsTo' },
    'activeAdsRangeConfigAverage': { from: 'activeAdsFrom', to: 'activeAdsTo' },
    'totalUsers': { from: 'totalUsersFrom', to: 'totalUsersTo' },
    'scoringRange': { from: 'scoreFrom', to: 'scoreTo' },
    'countWatchRange': { from: 'viewsFrom', to: 'viewsTo' },
    'countAddFavoriteRange': { from: 'favoritesFrom', to: 'favoritesTo' },
    'countTimeInSectionRange': { from: 'timeFrom', to: 'timeTo' },
    'countMessages': { from: 'messagesFrom', to: 'messagesTo' },
    'scoringLeadConfig': { from: 'leadsScoreFrom', to: 'leadsScoreTo' },
    'scoringSellerConfig': { from: 'sellersScoreFrom', to: 'sellersScoreTo' },
    'timeInSectionRangeConfigTotal': { from: 'timeFrom', to: 'timeTo' },
    'avgTime': { from: 'avgTimeFrom', to: 'avgTimeTo' },
    'countTimeRange': { from: 'timeFrom', to: 'timeTo' },
    'countWatchAdRange': { from: 'viewsFrom', to: 'viewsTo' },
    'countFavoriteAddRange': { from: 'favoritesFrom', to: 'favoritesTo' },
};

const tableFieldToFilterValues: Record<string, { from: keyof FilterValues; to: keyof FilterValues }> = {
    'totalAdViews': { from: 'viewsFrom', to: 'viewsTo' },
    'totalFavorites': { from: 'favoritesFrom', to: 'favoritesTo' },
    'time': { from: 'timeFrom', to: 'timeTo' },
    'totalActiveAds': { from: 'activeAdsFrom', to: 'activeAdsTo' },
    'totalArchivedAds': { from: 'archivedAdsFrom', to: 'archivedAdsTo' },
    'totalUsers': { from: 'totalUsersFrom', to: 'totalUsersTo' },
    'avgTime': { from: 'avgTimeFrom', to: 'avgTimeTo' },
    'globalScore': { from: 'scoreFrom', to: 'scoreTo' },
    'leadScoring': { from: 'leadsScoreFrom', to: 'leadsScoreTo' },
    'sellerScoring': { from: 'sellersScoreFrom', to: 'sellersScoreTo' },
    'leadCount': { from: 'leadsScoreFrom', to: 'leadsScoreTo' },
    'sellerCount': { from: 'sellersScoreFrom', to: 'sellersScoreTo' },
    'totalFavoritesAdded': { from: 'favoritesFrom', to: 'favoritesTo' },
    'timeStats': { from: 'timeFrom', to: 'timeTo' },
    'adViewsCount': { from: 'viewsFrom', to: 'viewsTo' },
    'favoritesIds': { from: 'favoritesFrom', to: 'favoritesTo' },
    'messagesSentCount': { from: 'messagesFrom', to: 'messagesTo' },
    'dwellTimeMinutes': { from: 'timeFrom', to: 'timeTo' },
    'activeAdsCount': { from: 'activeAdsFrom', to: 'activeAdsTo' },
    'localScore': { from: 'scoreFrom', to: 'scoreTo' },
    'totalTimeMinutesInCategories': { from: 'timeFrom', to: 'timeTo' },
    'totalMessages': { from: 'messagesFrom', to: 'messagesTo' },
    'totalMessagesSent': { from: 'messagesFrom', to: 'messagesTo' },
    'totalDwellTimeMinutes': { from: 'timeFrom', to: 'timeTo' },
};

export default function ScoringList() {

    const [searchParams, setSearchParams] = useSearchParams();
    const rawTabType = searchParams.get('type');
    const tabType = useMemo<ScoringTabType>(
        () => (rawTabType as ScoringTabType) || "users",
        [rawTabType]
    );

    const [pageIndex, setPageIndex] = React.useState(1)

    const queryClient = useQueryClient();

    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500)

    const [selectedRole, setSelectedRole] = useState<ScoringRole | undefined>(undefined);
    const [selectedTableType, setSelectedTableType] = useState<ScoringTableType>(ScoringTableType.AGGREGATED);

    const { isLoading: isLoadingExport, exportAction } = useExportData();
    const { setSuccess } = useAlert();

    const [columnsStructure, setColumnsStructure] = useState<TableColumn<any>[]>([]);
    const [filterConfig, setFilterConfig] = useState<FilterConfig | undefined>(undefined);
    const [filterParams, setFilterParams] = useState<ScoringLeadsRequest | undefined>(undefined);

    const [isLoadingScoringData, setIsLoadingScoringData] = useState(false);
    const [scoringError, setScoringError] = useState<Error | null>(null);
    const [isScoringError, setIsScoringError] = useState(false);
    const [isFetchedScoringData, setIsFetchedScoringData] = useState(false);
    const [scoringData, setScoringData] = useState<any>(null);

    const [filterOpened, setFilterOpened] = useState(false);
    const [priceFilterOpened, setPriceFilterOpened] = useState(false);
    const [dateFilterOpened, setDateFilterOpened] = useState(false);
    const [selectFilterOpened, setSelectFilterOpened] = useState<string | null>(null);
    const [detailedSelectFilterOpened, setDetailedSelectFilterOpened] = useState<string | null>(null);

    const [filterValues, setFilterValues] = useState<FilterValues>(INITIAL_FILTER_VALUES);

    // Временные значения фильтров колонок (для ввода)
    const [tempColumnFilters, setTempColumnFilters] = useState<Record<string, { from: string; to: string }>>({});
    // Примененные значения фильтров колонок (для запроса)
    const [appliedColumnFilters, setAppliedColumnFilters] = useAppliedColumnFilters();
    const [openColumnFilter, setOpenColumnFilter] = useState<string | null>(null);
    // Country name request (from google analytics)
    const [appliedColumnCountryNameFilters, setAppliedColumnCountryNameFilters] = useAppliedColumnCountryNameFilters();
    const [tempColumnCountryNameFilters, setTempColumnCountryNameFilters] = useState<Record<string, any[]>>({});
    const [opeCountryNameColumnFilter, setOpenCountryNameColumnFilter] = useState<string | null>(null);
    // Country id request (from backend)
    const [appliedColumnCountryIdFilters, setAppliedColumnCountryIdFilters] = useAppliedColumnCountryIdFilters();
    const [tempColumnCountryIdFilters, setTempColumnCountyIdFilters] = useState<Record<string, any[]>>({});
    const [openCountryIdColumnFilter, setOpenCountryIdColumnFilter] = useState<string | null>(null);

    const {
        loadedOptions,
        loadingStates,
        filterHasMore,
        filterLoadingMore,
        loadFilterOptions,
        handleLoadMore,
        onSearch,
    } = useFilterOptions({ values: filterValues });


    const paramsForScoringFilters = useCallback(() => {
        const params = createScoringRequestParam();
        return {
            ...params,
            limit: pageSize,
            offset: (pageIndex - 1) * pageSize,
        };
    }, [pageSize, pageIndex, filterParams, selectedRole, appliedColumnFilters]);

    useEffect(() => {
        setFilterValues(INITIAL_FILTER_VALUES);
        setFilterParams(undefined);
        setTempColumnFilters({});
        setAppliedColumnFilters({});
        setAppliedColumnCountryNameFilters({});
        setTempColumnCountryNameFilters({});
        setSearchQuery('')
        setPageIndex(1);
    }, [tabType]);

    useEffect(() => {
        const params = createScoringRequestParam();
        const abortController = new AbortController();

        const fetchData = async () => {
            setIsLoadingScoringData(true);
            setIsFetchedScoringData(false);
            setIsScoringError(false);
            setScoringError(null);

            try {
                const data = await fetchScoringLeads(
                    tabType,
                    selectedTableType,
                    {
                        ...params,
                        limit: pageSize,
                        offset: (pageIndex - 1) * pageSize,
                    },
                    abortController.signal
                );

                if (!abortController.signal.aborted) {
                    setScoringData(data);
                    setIsFetchedScoringData(true);
                    setIsLoadingScoringData(false);
                }
            } catch (error) {
                const err = error as Error;
                if (!abortController.signal.aborted && err.message !== 'Request canceled') {
                    setScoringError(err);
                    setIsScoringError(true);
                    setIsLoadingScoringData(false);
                }
            }
        };

        fetchData();

        return () => {
            abortController.abort();
        };
    }, [
        tabType,
        selectedTableType,
        selectedRole,
        pageIndex,
        filterParams,
        appliedColumnFilters,
        appliedColumnCountryNameFilters,
        appliedColumnCountryIdFilters,
        debouncedSearchQuery,
    ]);

    const filterCallbacks: FilterCallbacks = {
        onFilterChange: (key, value) => {
            setFilterValues(prev => ({
                ...prev,
                [key]: value
            }));
        },
        onPriceChange: (from, to) => {
            setFilterValues(prev => ({
                ...prev,
                price: { from, to }
            }));
        },
        onDateChange: (from, to) => {
            setFilterValues(prev => ({
                ...prev,
                date: { from, to }
            }));
        }
    };

    const handleDismiss = () => {
        setFilterValues(INITIAL_FILTER_VALUES);
        setFilterParams(undefined);
        setTempColumnFilters({});
        setAppliedColumnFilters({});
        setAppliedColumnCountryNameFilters({});
        setTempColumnCountryNameFilters({});
        setAppliedColumnCountryIdFilters({});
        setTempColumnCountyIdFilters({});
    };

    const createScoringRequestParam = (): Omit<ScoringLeadsRequest, 'limit' | 'offset'> => {
        const dateFrom = filterValues.date?.from ? toDateFormat(filterValues.date?.from) : undefined;
        const dateTo = filterValues.date?.to ? toDateFormat(filterValues.date?.to) : undefined;

        // Разделяем страны и города для фильтров по имени
        const columnCountriesName: string[] = [];
        const columnCitiesName: string[] = [];

        Object.entries(appliedColumnCountryNameFilters).forEach(([field, countries]) => {
            const values = countries.map(c =>
                typeof c.value === 'string' ? c.value : c.value?.value
            ).filter(Boolean);

            // Определяем куда складывать значения в зависимости от поля
            if (field === 'geography' || field === 'country' || field === 'currentCountry') {
                columnCountriesName.push(...values);
            } else if (field === 'city' || field === 'currentCity') {
                columnCitiesName.push(...values);
            }
        });

        // Разделяем страны и города для фильтров по ID
        const columnCountriesId: string[] = [];
        const columnCitiesId: string[] = [];

        Object.entries(appliedColumnCountryIdFilters).forEach(([field, countries]) => {
            const values = countries.map(c => c.value?.id || c.id).filter(Boolean);

            if (field === 'regCountryTitle') {
                columnCountriesId.push(...values);
            } else if (field === 'regCityTitle') {
                columnCitiesId.push(...values);
            }
        });

        console.log('appliedColumnCountryIdFilters:', appliedColumnCountryIdFilters);
        console.log('columnCountriesId:', columnCountriesId);
        console.log('columnCitiesId:', columnCitiesId);
        console.log('filterValues.filterValues:', filterValues);

        const params: Omit<ScoringLeadsRequest, 'limit' | 'offset'> = {
            orderBy: undefined,
            dateFrom: dateFrom,
            dateTo: dateTo,
            userId: undefined,
            userSearch: debouncedSearchQuery.length > 0 ? debouncedSearchQuery : undefined,
            userRole: selectedRole ? [selectedRole] : undefined,
            userSegment: filterValues.scoringUserType?.map(el => el),
            regCountryId: [
                ...(filterValues.countryResponseForId?.map(el => el.id) || []),
                ...columnCountriesId
            ].filter((v, i, a) => a.indexOf(v) === i),
            currentCountry: [
                ...(filterValues.countryResponseForName?.map(el => el.value) || []),
                ...columnCountriesName
            ].filter((v, i, a) => a.indexOf(v) === i),
            regCityId: [
                ...(filterValues.cityResponseForId?.map(el => el.id) || []),
                ...columnCitiesId
            ].filter((v, i, a) => a.indexOf(v) === i),
            currentCity: [
                ...(filterValues.cityResponseForName?.map(el => el.value) || []),
                ...columnCitiesName
            ].filter((v, i, a) => a.indexOf(v) === i),
            categoryId: filterValues.category?.map(el => el.id),
            catalogId: filterValues.subcategory?.map(el => el.id),
            scoreFrom: filterValues.scoreFrom ? Number(filterValues.scoreFrom) : undefined,
            scoreTo: filterValues.scoreTo ? Number(filterValues.scoreTo) : undefined,
            viewsFrom: filterValues.viewsFrom ? Number(filterValues.viewsFrom) : undefined,
            viewsTo: filterValues.viewsTo ? Number(filterValues.viewsTo) : undefined,
            favoritesFrom: filterValues.favoritesFrom ? Number(filterValues.favoritesFrom) : undefined,
            favoritesTo: filterValues.favoritesTo ? Number(filterValues.favoritesTo) : undefined,
            messagesFrom: filterValues.messagesFrom ? Number(filterValues.messagesFrom) : undefined,
            messagesTo: filterValues.messagesTo ? Number(filterValues.messagesTo) : undefined,
            timeFrom: filterValues.timeFrom ? Number(filterValues.timeFrom) : undefined,
            timeTo: filterValues.timeTo ? Number(filterValues.timeTo) : undefined,
            activeAdsFrom: filterValues.activeAdsFrom ? Number(filterValues.activeAdsFrom) : undefined,
            activeAdsTo: filterValues.activeAdsTo ? Number(filterValues.activeAdsTo) : undefined,
            archivedAdsFrom: filterValues.archivedAdsFrom ? Number(filterValues.archivedAdsFrom) : undefined,
            archivedAdsTo: filterValues.archivedAdsTo ? Number(filterValues.archivedAdsTo) : undefined,
            totalUsersFrom: filterValues.totalUsersFrom ? Number(filterValues.totalUsersFrom) : undefined,
            totalUsersTo: filterValues.totalUsersTo ? Number(filterValues.totalUsersTo) : undefined,
            avgTimeFrom: filterValues.avgTimeFrom ? Number(filterValues.avgTimeFrom) : undefined,
            avgTimeTo: filterValues.avgTimeTo ? Number(filterValues.avgTimeTo) : undefined,
            leadsScoreFrom: filterValues.leadsScoreFrom ? Number(filterValues.leadsScoreFrom) : undefined,
            leadsScoreTo: filterValues.leadsScoreTo ? Number(filterValues.leadsScoreTo) : undefined,
            sellersScoreFrom: filterValues.sellersScoreFrom ? Number(filterValues.sellersScoreFrom) : undefined,
            sellersScoreTo: filterValues.sellersScoreTo ? Number(filterValues.sellersScoreTo) : undefined,
        };

        return params;
    }

    const handleApply = () => {
        const params = createScoringRequestParam();
        setFilterParams(params as any);
        setPageIndex(1);
    };

    useCleanPaginationURL();
    usePaginationTextReplace()

    const downloadScoring = async () => {
        try {
            const method = () => exportScoring(
                tabType,
                selectedTableType,
                {
                    ...paramsForScoringFilters(),
                    limit: undefined,
                    offset: undefined
                }
            )
            const result = await exportAction(method, "scoring", "xlsx");
            if (result.success) {
                setSuccess('Загрузка отчета успешно началась');
            }
        } catch (err) { }
    }

    useEffect(() => {
        queryClient.invalidateQueries({ queryKey: [pageIndex] })
    }, [pageIndex]);

    const dataWithIds = (scoringData?.list.data ?? []).map((item: any, index: number) => ({
        ...item,
        id: index.toString()
    }));

    const isActiveNavFilter = () => {
        const hasPriceFilter = filterValues.price?.from?.trim() !== '' || filterValues.price?.to?.trim() !== '';
        const hasDateFilter = filterValues.date?.from?.trim() !== '' || filterValues.date?.to?.trim() !== '';

        const currentCountryNameFilters = getAppliedColumnCountryNameFilters();
        const currentCountryIdFilters = getAppliedColumnCountryIdFilters();

        console.log('opened....appliedColumnCountryNameFilters', currentCountryNameFilters);
        console.log('opened....appliedColumnCountryIdFilters', currentCountryIdFilters);

        const hasGeoFilter =
            (filterValues.countryResponseForName?.length ?? 0) > 0 ||
            (filterValues.cityResponseForName?.length ?? 0) > 0 ||
            (filterValues.countryResponseForId?.length ?? 0) > 0 ||
            (filterValues.cityResponseForId?.length ?? 0) > 0 ||
            Object.keys(currentCountryNameFilters).length > 0 ||
            Object.keys(currentCountryIdFilters).length > 0
        console.log('isActiveNavFilter', hasGeoFilter, filterValues)
        const hasCategoryFilter =
            (filterValues.category?.length ?? 0) > 0 ||
            (filterValues.subcategory?.length ?? 0) > 0;

        const hasSelectFilters = filterConfig?.selectFilters?.some(filter => {
            if (filter.key === 'geography' || filter.key === 'category-subcategory') {
                return false;
            }
            const value = filterValues[filter.key as keyof FilterValues];
            const hasValue = Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined;
            return hasValue;
        });

        const result = hasPriceFilter || hasDateFilter || hasSelectFilters || hasGeoFilter || hasCategoryFilter;

        return result;
    };

    const isActiveFilterField = (field: string) => {
        const countryNameFields = ['geography', 'country', 'currentCountry'];
        const countryIdFields = ['regCountryTitle'];

        if (field === 'currentCountry') {
            const filters = getAppliedColumnCountryNameFilters();
            return (filters['currentCountry']?.length ?? 0) > 0;
        }

        if (countryNameFields.includes(field)) {
            const filters = getAppliedColumnCountryNameFilters();
            return filters[field]?.length > 0;
        }

        if (countryIdFields.includes(field)) {
            const filters = getAppliedColumnCountryIdFilters();
            return filters[field]?.length > 0;
        }

        // Проверка для числовых range фильтров из filterValues
        const filterFields = tableFieldToFilterValues[field];
        if (filterFields) {
            const fromValue = filterValues[filterFields.from];
            const toValue = filterValues[filterFields.to];
            return Boolean(fromValue) || Boolean(toValue);
        }

        return false;
    };

    const handleFilterTap = (field: string) => {
        // Проверяем по id - это географический фильтр или числовой
        console.log('handleFilterTap', field)
        switch (field) {
            case 'geography':
            case 'country':
            case 'currentCountry':
                // fetch NAME
                if (opeCountryNameColumnFilter !== field) {
                    setTempColumnCountryNameFilters(prev => ({
                        ...prev,
                        [field]: appliedColumnCountryNameFilters[field] || []
                    }));
                }
                setOpenCountryNameColumnFilter(field);
                loadFilterOptions('countryResponseForName');

                break
            case 'regCountryTitle':
                // fetch ID
                if (openCountryIdColumnFilter !== field) {
                    setTempColumnCountyIdFilters(prev => ({
                        ...prev,
                        [field]: appliedColumnCountryIdFilters[field] || []
                    }));
                }
                setOpenCountryIdColumnFilter(field);
                loadFilterOptions('countryResponseForId');

                break
            default:
                if (openColumnFilter !== field) {
                    setTempColumnFilters(prev => ({
                        ...prev,
                        [field]: appliedColumnFilters[field] || { from: '', to: '' }
                    }));
                }
                setOpenColumnFilter(field);
        }
    }

    const handleColumnFilterChange = (field: string, groupId: string, from: string, to: string) => {
        setTempColumnFilters(prev => ({
            ...prev,
            [field]: { from, to }
        }));
    };

    const handleColumnFilterApply = (group: NumberRangeGroup[]) => {
        console.log('handleColumnFilterApply group', group);
        group.forEach(item => {
            const fields = filterFieldsMap[item.id];
            if (fields) {
                filterValues[fields.from] = item.fromValue;
                filterValues[fields.to] = item.toValue;
            }
        });
        setOpenColumnFilter(null);
        setPageIndex(1);
        handleApply();
    };

    const handleColumnFilterClear = (group: NumberRangeGroup[]) => {
        console.log('handleColumnFilterClear group', group);
        group.forEach(item => {
            const fields = filterFieldsMap[item.id];
            if (fields) {
                filterValues[fields.from] = null;
                filterValues[fields.to] = null;
            }
        });
        setOpenColumnFilter(null);
        setPageIndex(1);
        handleApply();
    };

    const handleColumnFilterDismiss = () => {
        // Откатываем временные значения
        if (openColumnFilter) {
            setTempColumnFilters(prev => ({
                ...prev,
                [openColumnFilter]: appliedColumnFilters[openColumnFilter] || { from: '', to: '' }
            }));
        }
        setOpenColumnFilter(null);
    };

    // Обработчики для географических фильтров
    const handleColumnGeographyFilterSelect = (field: string, value: string) => {
        console.log('handleColumnGeographyFilterSelect', field, value);
        const items = loadedOptions['countryResponseForName'] || [];
        const currentValues = tempColumnCountryNameFilters[field] || [];
        const selectedValue = items.find(item => {
            const itemValue = typeof item.value === 'string' ? item.value : item.value?.value;
            return itemValue === value;
        });

        if (!selectedValue) {
            console.warn('Selected value not found:', value);
            return;
        }

        const newValues = currentValues.some(v => {
            const vValue = typeof v.value === 'string' ? v.value : v.value?.value;
            const selectedValueString = typeof selectedValue.value === 'string' ? selectedValue.value : selectedValue.value?.value;
            return vValue === selectedValueString;
        })
            ? currentValues.filter(v => {
                const vValue = typeof v.value === 'string' ? v.value : v.value?.value;
                const selectedValueString = typeof selectedValue.value === 'string' ? selectedValue.value : selectedValue.value?.value;
                return vValue !== selectedValueString;
            })
            : [...currentValues, selectedValue];

        setTempColumnCountryNameFilters(prev => ({
            ...prev,
            [field]: newValues
        }));
    };

    // Создаем единый набор обработчиков для географических фильтров
    const createCountryFilterHandlers = (
        filterType: 'name' | 'id',
        openFilter: string | null,
        setOpenFilter: (value: string | null) => void,
        tempFilters: Record<string, any[]>,
        setTempFilters: React.Dispatch<React.SetStateAction<Record<string, any[]>>>,
        appliedFilters: Record<string, any[]>,
        setAppliedFilters: React.Dispatch<React.SetStateAction<Record<string, any[]>>>,
        optionsKey: 'countryResponseForName' | 'countryResponseForId'
    ) => ({
        apply: (selectedValues: Set<string>) => {
            if (openFilter) {
                const selectedCountries = Array.from(selectedValues)
                    .map(value => {
                        const items = loadedOptions[optionsKey] || [];
                        return items.find((item: any) => {
                            const itemValue = typeof item.value === 'string'
                                ? item.value
                                : (item.value?.value || item.value?.id);
                            return itemValue === value;
                        });
                    })
                    .filter(Boolean);

                if (filterType === 'name') {
                    const mapped = selectedCountries.map((el: any) => ({
                        value: el.label,
                        label: el.label,
                        count: 0
                    }));
                    setFilterValues(prev => ({
                        ...prev,
                        countryResponseForName: mapped
                    }));

                    // Обновляем tempFilters, применение будет через handleApply
                    setTempFilters(prev => ({
                        ...prev,
                        [openFilter]: selectedCountries
                    }));
                }

                setTempFilters(prev => ({
                    ...prev,
                    [openFilter]: selectedCountries
                }));

                setAppliedFilters(prev => ({
                    ...prev,
                    [openFilter]: selectedCountries
                }));
            }
            setOpenFilter(null);
            setPageIndex(1);
        },

        clear: () => {
            if (openFilter) {
                if (filterType === 'name') {
                    setFilterValues(prev => ({
                        ...prev,
                        countryResponseForName: []
                    }));
                }
                setTempFilters(prev => ({
                    ...prev,
                    [openFilter]: []
                }));

                setAppliedFilters(prev => ({
                    ...prev,
                    [openFilter]: []
                }));
            }
            setOpenFilter(null);
            setPageIndex(1);
        },

        dismiss: () => {
            if (openFilter) {
                setTempFilters(prev => ({
                    ...prev,
                    [openFilter]: appliedFilters[openFilter] || []
                }));
            }
            setOpenFilter(null);
        }
    });

    // Используем в компоненте
    const countryNameHandlers = createCountryFilterHandlers(
        'name',
        opeCountryNameColumnFilter,
        setOpenCountryNameColumnFilter,
        tempColumnCountryNameFilters,
        setTempColumnCountryNameFilters,
        appliedColumnCountryNameFilters,
        setAppliedColumnCountryNameFilters,
        'countryResponseForName'
    );

    const countryIdHandlers = createCountryFilterHandlers(
        'id',
        openCountryIdColumnFilter,
        setOpenCountryIdColumnFilter,
        tempColumnCountryIdFilters,
        setTempColumnCountyIdFilters,
        appliedColumnCountryIdFilters,
        setAppliedColumnCountryIdFilters,
        'countryResponseForId'
    );

    const getColumnFilterConfig = (
        field: string
    ): NumberRangeGroup[] => {
        const dwellTimeMinutes = () => {
            return tabType === 'catalogs' && selectedTableType === 'detailed' ? countTimeInSubsectionRange : countWatchRange
        }

        const totalAdViews = () => {
            return tabType === 'users' ? (selectedTableType === 'aggregated' ? countWatchAdRange : countWatchRange) : countWatchRange
        }

        const time = () => {
            return tabType === 'catalogs' ? timeInCatalogRangeConfig : timeInSectionRangeConfig
        }

        const configMap: Record<string, NumberRangeGroup[]> = {
            'totalAdViews': [totalAdViews()],
            'totalFavorites': [countAddFavoriteRange],
            'time': time(),
            'totalActiveAds': [activeAdsNumberConfig],
            'totalArchivedAds': [archiveAdsNumberConfig],
            'globalScore': [scoringRange],
            'totalUsers': [countAllUsersRange],
            'leadScoring': [scoringLeadConfig],
            'sellerScoring': [scoringSellerConfig],
            'leadCount': [scoringLeadConfig],
            'sellerCount': [scoringSellerConfig],
            'totalFavoritesAdded': [countAddFavoriteRange],
            'timeStats': time(),
            'adViewsCount': [countWatchRange],
            'favoritesIds': [countFavoriteAddRange],
            'messagesSentCount': [countMessages],
            'dwellTimeMinutes': [dwellTimeMinutes()],
            'activeAdsCount': [activeAdsNumberConfig],
            'localScore': [scoringRange],
            'totalTimeMinutesInCategories': [countTimeInSectionRange],
            'totalMessages': [countMessages],
            'totalMessagesSent': [countMessages],
            'totalDwellTimeMinutes': [countTimeInSectionRange]
        };

        const baseConfig = configMap[field];
        if (!baseConfig) {
            return [];
        }

        return baseConfig.map(config => {
            // id конкретного элемента конфигурации
            const filterKeys = filterFieldsMap[config.id];
            const fromValue = filterKeys?.from ? filterValues[filterKeys.from] : '';
            const toValue = filterKeys?.to ? filterValues[filterKeys.to] : '';

            return {
                ...config,
                fromValue: fromValue ?? '',
                toValue: toValue ?? ''
            };
        });
    };

    const columns = createDataTableFilterColumns(
        columnsStructure,
        handleFilterTap,
        isActiveFilterField
    );

    const prevTabTypeRef = useRef(tabType);

    useEffect(() => {
        if (prevTabTypeRef.current !== tabType) {
            prevTabTypeRef.current = tabType;

            setSelectedTableType(ScoringTableType.AGGREGATED);
            const structure = columnsForCurrentTab(tabType, ScoringTableType.AGGREGATED);
            setColumnsStructure(structure as TableColumn<any>[]);
            setFilterConfig(scoringFilters);
            setPageIndex(1);
            return;
        }

        const structure = columnsForCurrentTab(tabType, selectedTableType);
        setColumnsStructure(structure as TableColumn<any>[]);
        setFilterConfig(scoringFilters);
        setPageIndex(1);
    }, [tabType, selectedTableType]);

    useEffect(() => {
        setFilterValues(INITIAL_FILTER_VALUES);
        setFilterParams(undefined);
        setTempColumnFilters({});
        setAppliedColumnFilters({});
        setAppliedColumnCountryNameFilters({});
        setTempColumnCountryNameFilters({});
        setAppliedColumnCountryIdFilters({});
        setTempColumnCountyIdFilters({});
        setPageIndex(1);
    }, [selectedTableType]);

    // Components
    const RoleMenu = () => (
        <ActionMenu>
            <ActionMenu.Button className='!bg-white'>{selectedRole ? getScoringRoleName(selectedRole) : 'Роль (все)'}</ActionMenu.Button>
            <ActionMenu.Overlay>
                <ActionList
                    selectionVariant="single"
                    role="menu"
                    aria-label="Item"
                >
                    {
                        <ActionList.Item
                            key='role-all'
                            role="menuitemradio"
                            selected={selectedRole === undefined}
                            aria-checked={selectedRole === undefined}
                            onSelect={() => setSelectedRole(undefined)}
                        >
                            <Stack align="center" direction="horizontal" gap='none'>
                                Все
                            </Stack>
                        </ActionList.Item>
                    }
                    {Object.values(ScoringRole).map((role) => {
                        return (
                            <ActionList.Item
                                key={role}
                                role="menuitemradio"
                                selected={selectedRole === role}
                                aria-checked={selectedRole === role}
                                onSelect={() => setSelectedRole(role)}
                            >
                                {getScoringRoleName(role)}
                            </ActionList.Item>
                        );
                    })}
                </ActionList>
            </ActionMenu.Overlay>
        </ActionMenu>
    )

    const TableType = () => (
        <ActionMenu>
            <ActionMenu.Button className='!bg-white'>{getTableTypeName(selectedTableType)}</ActionMenu.Button>
            <ActionMenu.Overlay>
                <ActionList
                    selectionVariant="single"
                    role="menu"
                    aria-label="Item"
                >
                    {Object.entries(ScoringTableType).map(([key, value]) => {
                        return (
                            <ActionList.Item
                                key={key}
                                role="menuitemradio"
                                selected={selectedTableType === value}
                                disabled={(tabType === 'categories' && value === 'detailed')}
                                aria-checked={selectedTableType === value}
                                onSelect={() => setSelectedTableType(value)}
                            >
                                {getTableTypeName(value)}
                            </ActionList.Item>
                        );
                    })}
                </ActionList>
            </ActionMenu.Overlay>
        </ActionMenu>
    )

    const Search = () => {
        const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

        // Синхронизация при внешнем изменении searchQuery
        useEffect(() => {
            setLocalSearchQuery(searchQuery);
        }, [searchQuery]);

        const handleSearch = () => {
            setSearchQuery(localSearchQuery);
            setPageIndex(1);
        };

        const handleClear = () => {
            setLocalSearchQuery('');
            setSearchQuery('');
            setPageIndex(1);
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                handleSearch();
            }
        };

        return (
            <TextInput
                className='w-[100%]'
                trailingAction={
                    <>
                        <IconButton
                            className='mt-[1px]'
                            onClick={handleSearch}
                            icon={SearchIcon}
                            size="small"
                            aria-label="Поиск" />
                        {localSearchQuery.length > 0 && (
                            <TextInput.Action
                                onClick={handleClear}
                                icon={XCircleFillIcon}
                                aria-label="Очистить"
                            />
                        )}
                    </>
                }
                value={localSearchQuery}
                placeholder='Поиск по ID и номеру регистрации'
                onChange={(e) => {
                    setLocalSearchQuery(e.target.value);
                }
                }
                onKeyDown={handleKeyDown}
            />
        );
    };

    const NumberColumnFilters = () => (
        columnsStructure.filter(col => col.filtered).map((col) => (
            <NumberRangeFormModal
                key={col.id}
                opened={openColumnFilter === col.id}
                isShowBack={false}
                setOpened={(value) => setOpenColumnFilter(value ? col.id : null)}
                groups={getColumnFilterConfig(col.id)}
                onGroupChange={(groupId, from, to) => handleColumnFilterChange(col.id, groupId, from, to)}
                onDismiss={handleColumnFilterDismiss}
                onApply={group => handleColumnFilterApply(group)}
                onClear={group => handleColumnFilterClear(group)}
                title={getColumnFilterConfig(col.id)?.[0]?.label || col.title}
            />
        ))
    )

    return (
        <RegularPageLayout
            header={
                <div className="overflow-x-auto">
                    <div className="flex flex-wrap gap-4">
                        <div className="h-8  w-[428px] shrink-0">
                            <Segment items={scoringTabsData} />
                        </div>
                        <div className=" w-[116px]! shrink-0">
                            <RoleMenu />
                        </div>
                        <div className="h-8  w-[184px]! shrink-0">
                            <TableType />
                        </div>
                        <div className=" h-8  flex-1 min-w-[400px]">
                            <Search />
                        </div>
                        <div className=" h-8  w-[32px] shrink-0">
                            {filterConfig && <ScoringFilterModal
                                key={`${tabType}-${selectedRole}-${selectedTableType}`}
                                filterOpened={filterOpened}
                                setFilterOpened={setFilterOpened}
                                priceFilterOpened={priceFilterOpened}
                                setPriceFilterOpened={setPriceFilterOpened}
                                dateFilterOpened={dateFilterOpened}
                                setDateFilterOpened={setDateFilterOpened}
                                selectFilterOpened={selectFilterOpened}
                                setSelectFilterOpened={setSelectFilterOpened}
                                detailedSelectFilterOpened={detailedSelectFilterOpened}
                                setDetailedSelectFilterOpened={setDetailedSelectFilterOpened}
                                config={filterConfig}
                                values={filterValues}
                                callbacks={filterCallbacks}
                                onDismiss={handleDismiss}
                                onApply={handleApply}
                                isActive={isActiveNavFilter() || false}
                            />}
                        </div>
                        <div className=" h-8  w-[165px] shrink-0">
                            <Button
                                variant="default"
                                loading={isLoadingExport}
                                leadingVisual={DownloadIcon}
                                onClick={downloadScoring}
                            >
                                Выгрузить отчет
                            </Button>
                        </div>
                    </div>
                </div>
            }
            content={
                <Stack>
                    <SelectFormModal
                        selectFilterOpened={opeCountryNameColumnFilter !== null}
                        setSelectFilterOpened={(isOpen) => setOpenCountryNameColumnFilter(isOpen ? opeCountryNameColumnFilter : null)}
                        label="Текущая страна"
                        searchPlaceholder="Поиск страны"
                        mode='confirm'
                        isShowBack={false}
                        items={loadedOptions['countryResponseForName'] || []}
                        selectedValues={new Set(
                            (() => {
                                if (!opeCountryNameColumnFilter) return [];
                                const selectedCountries = tempColumnCountryNameFilters[opeCountryNameColumnFilter] || [];

                                const captions = selectedCountries.map(el => {
                                    const value = el.value ?? el.label;
                                    if (typeof value === 'string') return value;
                                    if (typeof value === 'object' && value !== null) {
                                        return String(value.value ?? value.label ?? '');
                                    }
                                    return '';
                                });
                                return captions
                            })()
                        )}
                        onSelect={(value) => {
                            if (opeCountryNameColumnFilter !== null) {
                                handleColumnGeographyFilterSelect(opeCountryNameColumnFilter, value);
                            }
                        }}
                        onApply={countryNameHandlers.apply}
                        onClear={countryNameHandlers.clear}
                        onDismiss={countryNameHandlers.dismiss}
                        isLoading={loadingStates['countryResponseForName']}
                        hasMore={filterHasMore['countryResponseForName']}
                        isLoadingMore={filterLoadingMore['countryResponseForName']}
                        onLoadMore={() => handleLoadMore('countryResponseForName')}
                        onSearch={onSearch ? (query) => onSearch('countryResponseForName', query) : undefined}
                    />
                    <SelectFormModal
                        selectFilterOpened={openCountryIdColumnFilter !== null}
                        setSelectFilterOpened={(isOpen) => setOpenCountryIdColumnFilter(isOpen ? openCountryIdColumnFilter : null)}
                        label="Страна регистрации"
                        searchPlaceholder="Поиск страны"
                        mode='confirm'
                        isShowBack={false}
                        items={loadedOptions['countryResponseForId'] || []}
                        selectedValues={new Set(
                            (() => {
                                if (!openCountryIdColumnFilter) return [];
                                const selectedCountries = tempColumnCountryIdFilters[openCountryIdColumnFilter] || [];

                                return selectedCountries.map(el => el.label);
                            })()
                        )}
                        onSelect={(value) => {
                            if (openCountryIdColumnFilter !== null) {
                                handleColumnGeographyFilterSelect(openCountryIdColumnFilter, value);
                            }
                        }}
                        onApply={countryIdHandlers.apply}
                        onClear={countryIdHandlers.clear}
                        onDismiss={countryIdHandlers.dismiss}
                        isLoading={loadingStates['countryResponseForId']}
                        hasMore={filterHasMore['countryResponseForId']}
                        isLoadingMore={filterLoadingMore['countryResponseForId']}
                        onLoadMore={() => handleLoadMore('countryResponseForId')}
                        onSearch={onSearch ? (query) => onSearch('countryResponseForId', query) : undefined}
                    />
                    <NumberColumnFilters />

                    {isLoadingScoringData && <Table.Container>
                        <Table.Skeleton
                            aria-labelledby="scoring-loading"
                            rows={pageSize}
                            columns={metricsTableConfig(7).map((el) => {
                                return {
                                    header: el.header,
                                    id: el.field,
                                }
                            })}
                        />
                    </Table.Container>}
                    {/* {isFetchedScoringData && !isLoadingScoringData && scoringData?.list.data.length === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Ничего не было найдено'
                            description={emptyListDescription}
                            button={undefined}
                        />
                    )} */}
                    {isFetchedScoringData && scoringData /*&& scoringData.list.data.length > 0*/ &&
                        <Table.Container>
                            <DataTable
                                aria-labelledby="scoring-table"
                                data={dataWithIds}
                                columns={columns}
                            />
                        </Table.Container>
                    }
                    {isFetchedScoringData && scoringData && scoringData.list.data.length > 0 &&
                        (<div className="sticky bottom-0 bg-white border-t border-gray-200 z-10 -mt-4">
                            <Table.Pagination aria-label="pagination" pageSize={pageSize} totalCount={scoringData.totalCount} onChange={({
                                pageIndex
                            }) => {
                                setPageIndex(pageIndex + 1);
                            }} defaultPageIndex={pageIndex - 1} />
                        </div>
                        )}
                </Stack>
            }
        ></RegularPageLayout>
    )
}